import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'

import { Box, Button, CircularProgress, Grid, Switch, Typography } from '@mui/material'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import DownloadIcon from '@mui/icons-material/Download'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'

import {
  FormProvider,
  RHFDateField,
  RHFDateTimeField,
  RHFSelectField,
  RHFTextField,
} from '../../common/hook-form'
import { apiConstants, postGeneric } from '../../common/constants'
import { convertDate, convertDateTimeISO } from '../../helpers/UtilityHelper'
import { CourierFilter, MerchantFilterCached } from '../../common/ProshipFormComponents'
import { isAdmin } from '../../helpers/ReduxHelpers'
import PageTitle from '../../components/PageTitle'

import {
  PageWrapper,
  SectionCard,
  SectionHeader,
  SectionIconBox,
  SectionTitle,
  SectionSubtitle,
  StepRow,
  StepHeader,
  StepNumber,
  StepTitle,
  StepSubtitle,
  StepContent,
  StepActions,
  ToggleRow,
  ToggleHelper,
  SlotPanel,
  ResultBar,
  DownloadChip,
} from './styled'
import { FormField, ToggleField, modernInputsSx, ctaBtnSx, ghostBtnSx } from './_downloadUi'

const ShipmentType = [
  { label: 'B2B', value: 'B2B' },
  { label: 'B2C', value: 'B2C' },
]

const BulkDownloadInvoice = () => {
  const { enqueueSnackbar } = useSnackbar()
  const merchantId = useSelector((s) => s.user.userInfo?.merchant?.id)
  const [isDownloading, setIsDownloading] = useState(false)
  const [showSlot, setShowSlot] = useState(false)
  const [pdfUrl, setPdfUrl] = useState('')

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape(
        {
          date: Yup.date().typeError('Date is required').required('Date is required'),
          fromTime: Yup.date()
            .nullable()
            .typeError('Invalid time')
            .when('toTime', {
              is: (to) => !!to,
              then: (schema) => schema.required('From time is required'),
            }),
          toTime: Yup.date()
            .nullable()
            .typeError('Invalid time')
            .when('fromTime', {
              is: (from) => !!from,
              then: (schema) =>
                schema
                  .required('To time is required')
                  .test('after-from', 'To time must be after From time', function (toTime) {
                    const fromTime = this.parent.fromTime
                    if (!fromTime || !toTime) return true
                    return new Date(toTime).getTime() > new Date(fromTime).getTime()
                  }),
            }),
          shipmentType: Yup.string().required('Shipment type is required'),
          pincode: Yup.string()
            .required('Pincode is required')
            .matches(/^\d{6}$/, 'Please enter a valid 6 digit pincode'),
          merchant: isAdmin()
            ? Yup.string().required('Please select a merchant')
            : Yup.string().nullable(),
        },
        [['fromTime', 'toTime']]
      )
    ),
    defaultValues: {
      pincode: '',
      date: null,
      fromTime: null,
      toTime: null,
      shipmentType: '',
      courier: '',
      sku: '',
      merchant: '',
      cancelled_orders: false,
      manifested_orders: false,
    },
  })
  const { handleSubmit, reset, watch, setValue } = methods
  const fromTimeWatch = watch('fromTime')

  const handleSlotToggle = () => {
    if (showSlot) {
      setValue('fromTime', null)
      setValue('toTime', null)
    }
    setShowSlot((prev) => !prev)
  }

  const buildSlotDateTime = (date, time) => {
    const d = new Date(date)
    const t = new Date(time)
    d.setHours(t.getHours(), t.getMinutes(), 0, 0)
    return convertDateTimeISO(d)
  }

  const onSubmit = async (data) => {
    setIsDownloading(true)
    setPdfUrl('')
    try {
      const params = new URLSearchParams()
      if (showSlot && data.fromTime && data.toTime) {
        params.append('fromDateTime', buildSlotDateTime(data.date, data.fromTime))
        params.append('toDateTime', buildSlotDateTime(data.date, data.toTime))
      } else {
        params.append('date', convertDate(data.date))
      }
      params.append('pincode', data.pincode)
      params.append('merchantId', isAdmin() ? data.merchant : merchantId)
      params.append('shipmentType', data.shipmentType)
      params.append('includeCancelOrders', data.cancelled_orders)
      params.append('includeManifested', data.manifested_orders)
      if (data.courier) params.append('courierId', data.courier)
      if (data.sku) params.append('sku', data.sku)

      const response = await postGeneric(`${apiConstants.GENERATE_BULK_INVOICE}?${params.toString()}`)
      if (response?.data?.mergedPdf) {
        setPdfUrl(response.data.mergedPdf)
        window.open(response.data.mergedPdf, '_blank')
      } else {
        enqueueSnackbar('No invoices generated for the selected filters', { variant: 'warning' })
      }
    } catch (err) {
      console.error(err)
      const message = err?.response?.data?.message || err.message
      enqueueSnackbar(message || 'Error downloading file', { variant: 'error' })
    } finally {
      setIsDownloading(false)
    }
  }

  const handleReset = () => {
    reset()
    setPdfUrl('')
    if (showSlot) setShowSlot(false)
  }

  return (
    <>
      <PageTitle>Download Invoice (Pincode Wise)</PageTitle>
      <PageWrapper>
        <SectionCard>
          <SectionHeader>
            <SectionIconBox tone="primary">
              <ReceiptLongOutlinedIcon sx={{ fontSize: 16 }} />
            </SectionIconBox>
            <SectionTitle>Generate Invoices by Pickup Pincode</SectionTitle>
            <SectionSubtitle>D2C &bull; B2B or B2C</SectionSubtitle>
          </SectionHeader>

          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Box sx={modernInputsSx}>
              {/* ── Step 1: Where & When ── */}
              <StepRow>
                <StepHeader>
                  <StepNumber>1</StepNumber>
                  <Box>
                    <StepTitle>Where &amp; When</StepTitle>
                    <StepSubtitle>Pickup pincode, date, and shipment type</StepSubtitle>
                  </Box>
                </StepHeader>
                <StepContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormField label="Pickup Pincode" required>
                        <RHFTextField name="pincode" label="" placeholder="e.g. 560001" size="small" />
                      </FormField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormField label="Order Created Date" required>
                        <RHFDateField name="date" label="" size="small" fullWidth />
                      </FormField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormField label="Shipment Type" required>
                        <RHFSelectField name="shipmentType" label="" size="small" options={ShipmentType} width={'100%'} />
                      </FormField>
                    </Grid>
                  </Grid>
                </StepContent>
              </StepRow>

              {/* ── Step 2: Who & What ── */}
              <StepRow>
                <StepHeader>
                  <StepNumber>2</StepNumber>
                  <Box>
                    <StepTitle>Who &amp; What</StepTitle>
                    <StepSubtitle>Merchant, courier, and SKU filters</StepSubtitle>
                  </Box>
                </StepHeader>
                <StepContent>
                  <Grid container spacing={2}>
                    {isAdmin() && (
                      <Grid item xs={12} sm={6} md={4}>
                        <FormField label="Merchant" required>
                          <MerchantFilterCached required placeholder="Select Merchant" />
                        </FormField>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={4}>
                      <FormField label="Courier Partner">
                        <CourierFilter label="" placeholder="All couriers" />
                      </FormField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormField label="SKU" hint="Optional — filter to a single SKU">
                        <RHFTextField name="sku" label="" placeholder="e.g. SKU123" size="small" />
                      </FormField>
                    </Grid>
                  </Grid>
                </StepContent>
              </StepRow>

              {/* ── Step 3: Options ── */}
              <StepRow>
                <StepHeader>
                  <StepNumber muted>3</StepNumber>
                  <Box>
                    <StepTitle>Options</StepTitle>
                    <StepSubtitle>Time slot and what to include in the PDF</StepSubtitle>
                  </Box>
                </StepHeader>
                <StepContent>
                  <Box sx={{ mb: 2 }}>
                    <ToggleRow active={showSlot ? 1 : 0}>
                      <AccessTimeIcon sx={{ fontSize: 15, color: showSlot ? 'primary.main' : 'text.secondary' }} />
                      <Typography variant="caption" sx={{ fontWeight: 600, color: showSlot ? 'primary.main' : 'text.secondary' }}>
                        Filter by time slot
                      </Typography>
                      <ToggleHelper>Leave off for a full-day query</ToggleHelper>
                      <Switch checked={showSlot} onChange={handleSlotToggle} size="small" color="primary" />
                    </ToggleRow>
                    <SlotPanel in={showSlot}>
                      <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormField label="From Time">
                            <RHFDateTimeField name="fromTime" label="" mode="time" size="small" fullWidth />
                          </FormField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormField label="To Time">
                            <RHFDateTimeField name="toTime" label="" mode="time" size="small" fullWidth isEndTime startTime={fromTimeWatch} />
                          </FormField>
                        </Grid>
                      </Grid>
                    </SlotPanel>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <ToggleField name="cancelled_orders" label="Cancelled Orders" icon={<CancelOutlinedIcon />} />
                    <ToggleField name="manifested_orders" label="Manifested Orders" icon={<Inventory2OutlinedIcon />} />
                  </Box>
                </StepContent>
              </StepRow>
            </Box>

            {/* ── Actions ── */}
            <StepActions>
              <Button
                type="submit"
                variant="contained"
                size="small"
                disableElevation
                disabled={isDownloading}
                sx={ctaBtnSx}
                startIcon={isDownloading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <DownloadIcon fontSize="small" />}
              >
                {isDownloading ? 'Generating…' : 'Generate & Download'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleReset}
                disabled={isDownloading}
                sx={ghostBtnSx}
                startIcon={<RestartAltIcon fontSize="small" />}
              >
                Reset
              </Button>
            </StepActions>
          </FormProvider>
        </SectionCard>

        {pdfUrl && (
          <SectionCard>
            <SectionHeader>
              <SectionIconBox tone="success">
                <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
              </SectionIconBox>
              <SectionTitle>Invoice PDF Ready</SectionTitle>
              <SectionSubtitle>Click to open or re-download</SectionSubtitle>
            </SectionHeader>
            <Box sx={{ p: 2 }}>
              <ResultBar>
                <DownloadChip
                  tone="success"
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<PictureAsPdfOutlinedIcon sx={{ fontSize: 14 }} />}
                >
                  Download Merged PDF
                </DownloadChip>
              </ResultBar>
            </Box>
          </SectionCard>
        )}
      </PageWrapper>
    </>
  )
}

export default BulkDownloadInvoice
